// frontend/src/pages/MuseumPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { useAppSelector } from '../store/hooks';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Museum as MuseumIcon } from '@mui/icons-material';

interface Museum {
  id: number;
  name: string;
  museum_code: string;
  number_of_levels: string[];
  created_at: string;
  updated_at: string;
}

const MuseumPage: React.FC = () => {
  const [museums, setMuseums] = useState<Museum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMuseum, setEditingMuseum] = useState<Museum | null>(null);
  const [museumForm, setMuseumForm] = useState({
    name: '',
    museum_code: '',
    number_of_levels: ['1'],
  });
  const [selectOpen, setSelectOpen] = useState(false);

  const levelOptions = ['B', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  // Validate museum form
  const validateMuseumForm = (): { isValid: boolean; message: string } => {
    if (!museumForm.name.trim()) {
      return { isValid: false, message: 'Museum name is required' };
    }
    if (!museumForm.museum_code.trim()) {
      return { isValid: false, message: 'Museum code is required' };
    }
    if (!/^[A-Z]{2,10}$/.test(museumForm.museum_code)) {
      return { isValid: false, message: 'Museum code must be 2-10 uppercase letters (e.g., ACM, NHB)' };
    }
    return { isValid: true, message: 'All fields are valid' };
  };

  const museumFormValidation = validateMuseumForm();

  // Get current user from Redux store
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin; // Only Admin can edit, Super User has same permissions as User

  useEffect(() => {
    fetchMuseums();
  }, []);

  const fetchMuseums = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/museums/', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch museums');
      }

      const data: unknown = await response.json();
      const museumsData =
        typeof data === 'object' && data && 'results' in data
          ? (data as { results?: Museum[] }).results || []
          : (data as Museum[]);
      setMuseums(museumsData);
    } catch (err: unknown) {
      setError('Failed to fetch museums');
      console.error('Error fetching museums:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMuseum = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/museums/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(museumForm),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create museum');
      }

      setSuccessMessage('Museum created successfully');
      setDialogOpen(false);
      resetForm();
      fetchMuseums();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create museum';
      setError(message);
    }
  };

  const handleUpdateMuseum = async () => {
    if (!editingMuseum) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/museums/${editingMuseum.id}/`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(museumForm),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update museum');
      }

      setSuccessMessage('Museum updated successfully');
      setDialogOpen(false);
      setEditingMuseum(null);
      resetForm();
      fetchMuseums();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update museum';
      setError(message);
    }
  };

  const handleDeleteMuseum = async (museum: Museum) => {
    if (!window.confirm(`Are you sure you want to delete ${museum.name}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/museums/${museum.id}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete museum');
      }

      setSuccessMessage('Museum deleted successfully');
      fetchMuseums();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete museum';
      setError(message);
    }
  };

  const handleEditMuseum = (museum: Museum) => {
    setEditingMuseum(museum);
    setMuseumForm({
      name: museum.name,
      museum_code: museum.museum_code,
      number_of_levels: Array.isArray(museum.number_of_levels)
        ? museum.number_of_levels
        : museum.number_of_levels
          ? [museum.number_of_levels]
          : ['1'],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setMuseumForm({
      name: '',
      museum_code: '',
      number_of_levels: ['1'],
    });
    setEditingMuseum(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMuseum(null);
    resetForm();
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccessMessage(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          <MuseumIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Museum Management
        </Typography>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Museum
          </Button>
        )}
      </Box>

      {/* Museum List */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {museums.map(museum => (
          <Box key={museum.id} sx={{ flex: '1 1 300px', minWidth: '300px', maxWidth: '400px' }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <MuseumIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" component="h3" gutterBottom>
                  {museum.name}
                </Typography>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Code: {museum.museum_code}
                </Typography>
                <Box display="flex" gap={1} mt={2}>
                  {canEdit && (
                    <>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditMuseum(museum)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteMuseum(museum)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Create/Edit Museum Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMuseum ? 'Edit Museum' : 'Create New Museum'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Museum Name"
            fullWidth
            variant="outlined"
            value={museumForm.name}
            onChange={e => setMuseumForm({ ...museumForm, name: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            label="Museum Code"
            fullWidth
            variant="outlined"
            value={museumForm.museum_code}
            onChange={e => setMuseumForm({ ...museumForm, museum_code: e.target.value.toUpperCase() })}
            sx={{ mb: 1 }}
            helperText={museumFormValidation.message}
            error={!museumFormValidation.isValid && (museumForm.name.length > 0 || museumForm.museum_code.length > 0)}
            required
            placeholder="e.g., ACM, NHB"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            💡 Museum code must be 2-10 uppercase letters and will be used for robot management
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel>Occupied Floors</InputLabel>
            <Select
              multiple
              open={selectOpen}
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              value={museumForm.number_of_levels}
              onChange={e => setMuseumForm({ ...museumForm, number_of_levels: e.target.value as string[] })}
              input={<OutlinedInput label="Occupied Floors" />}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 224,
                    width: 250,
                  },
                },
              }}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map(value => (
                    <Chip key={value} label={value === 'B' ? 'Basement' : `Level ${value}`} size="small" />
                  ))}
                </Box>
              )}
            >
              {levelOptions.map(level => (
                <MenuItem key={level} value={level}>
                  {level === 'B' ? 'Basement' : `Level ${level}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            💡 Select all floors that this museum occupies
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={editingMuseum ? handleUpdateMuseum : handleCreateMuseum}
            variant="contained"
            disabled={!museumFormValidation.isValid}
          >
            {editingMuseum ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MuseumPage;
